/**
 * FAQ Accordion Component
 * Implements W3C Accordion Pattern for accessibility
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */

export class FAQAccordion {
  private accordionItems: NodeListOf<HTMLElement>;
  private allowMultiple: boolean;

  constructor(allowMultiple: boolean = false) {
    this.accordionItems = document.querySelectorAll('[data-faq-item]');
    this.allowMultiple = allowMultiple;
    this.init();
  }

  private init(): void {
    this.accordionItems.forEach((item) => {
      const button = item.querySelector('[data-faq-button]') as HTMLButtonElement;
      if (!button) return;

      // Click handler
      button.addEventListener('click', () => {
        this.togglePanel(item, button);
      });

      // Keyboard navigation
      button.addEventListener('keydown', (e: KeyboardEvent) => {
        this.handleKeyboard(e, item, button);
      });
    });
  }

  private togglePanel(item: HTMLElement, button: HTMLButtonElement): void {
    const panel = item.querySelector('[data-faq-panel]') as HTMLElement;
    if (!panel) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    // If not allowing multiple open panels, close all others
    if (!this.allowMultiple && !isExpanded) {
      this.closeAllPanels();
    }

    // Toggle current panel
    if (isExpanded) {
      this.closePanel(button, panel);
    } else {
      this.openPanel(button, panel);
    }
  }

  private openPanel(button: HTMLButtonElement, panel: HTMLElement): void {
    button.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');

    // Get the actual content height
    const content = panel.querySelector('div') as HTMLElement;
    if (content) {
      const height = content.scrollHeight;
      panel.style.height = `${height}px`;
    }
  }

  private closePanel(button: HTMLButtonElement, panel: HTMLElement): void {
    button.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
    panel.style.height = '0px';
  }

  private closeAllPanels(): void {
    this.accordionItems.forEach((item) => {
      const button = item.querySelector('[data-faq-button]') as HTMLButtonElement;
      const panel = item.querySelector('[data-faq-panel]') as HTMLElement;
      if (button && panel) {
        this.closePanel(button, panel);
      }
    });
  }

  private handleKeyboard(e: KeyboardEvent, item: HTMLElement, button: HTMLButtonElement): void {
    const buttons = Array.from(this.accordionItems).map(
      (item) => item.querySelector('[data-faq-button]') as HTMLButtonElement
    );
    const currentIndex = buttons.indexOf(button);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Focus next button
        const nextIndex = (currentIndex + 1) % buttons.length;
        buttons[nextIndex]?.focus();
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Focus previous button
        const prevIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        buttons[prevIndex]?.focus();
        break;

      case 'Home':
        e.preventDefault();
        // Focus first button
        buttons[0]?.focus();
        break;

      case 'End':
        e.preventDefault();
        // Focus last button
        buttons[buttons.length - 1]?.focus();
        break;
    }
  }
}

